import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, UserPlus, Target, Users, Handshake, ChevronRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { recruitmentPartners } from '../data/mock';

export default function RecruitmentPage() {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-navy-50 via-white to-gold-50/30">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-0 w-96 h-96 bg-gold-100/30 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <nav className="flex items-center gap-2 text-sm text-navy-500 mb-6">
              <Link to="/" className="hover:text-brand-green transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-navy-500">Programs</span>
              <ChevronRight className="w-4 h-4" />
              <span className="text-navy-900 font-medium">Recruitment</span>
            </nav>
            
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold-50 border border-gold-200 mb-6">
              <UserPlus className="w-4 h-4 text-brand-gold" />
              <span className="text-sm font-medium text-gold-700">Recruitment Program</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold text-navy-900 mb-6">
              Building a{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-gold to-gold-600">
                Stronger Community
              </span>
            </h1>
            <p className="text-lg text-navy-600 leading-relaxed">
              At DC Men, we are dedicated to fostering the next generation of leaders through our comprehensive recruitment initiatives.
            </p>
          </div>
        </div>
      </section>

      {/* Join Our Mission Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-6">Join Our Mission</h2>
              <div className="space-y-4 text-navy-600 leading-relaxed">
                <p>
                  At DC Men in ECE, increasing the presence of men of color in early childhood classrooms is our primary goal. It allows children to see themselves in their teachers and have positive experiences with male role models, enriching their educational journey and personal development.
                </p>
                <p>
                  DC Men in ECE actively recruits men of color into early childhood education through strategic partnerships and the DC Leading Men Fellowship. We collaborate with local schools and educational programs, offering pathways to the Child Development Associate (CDA) credential and other forms of higher education.
                </p>
              </div>
              
              <div className="mt-8">
                <Link to="/contact">
                  <Button size="lg" className="bg-brand-gold hover:bg-gold-600 text-navy-900 font-semibold px-8 shadow-xl shadow-gold-500/20">
                    Join Our Program
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Image */}
            <div className="relative">
              <div className="bg-gradient-to-br from-gold-50 to-green-50 rounded-3xl p-4 shadow-xl overflow-hidden">
                <img 
                  src="/images/event-photo-1.jpg" 
                  alt="DC Men in ECE Recruitment Event"
                  className="w-full h-72 object-cover rounded-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Partners */}
      <section className="py-24 bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Our Recruitment Partners</h2>
            <p className="text-navy-300">We collaborate with leading educational institutions to identify and nurture talent.</p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {recruitmentPartners.map((partner, index) => (
              <div key={index} className="bg-navy-800/50 border border-navy-700/50 rounded-2xl p-6 text-center hover:border-brand-gold/30 transition-colors duration-300">
                <Handshake className="w-8 h-8 text-brand-gold mx-auto mb-3" />
                <p className="text-white font-medium">{partner}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comprehensive Approach */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-4">Our Comprehensive Approach</h2>
            <p className="text-navy-600">We provide a supportive and enriching environment where future leaders can thrive.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="group border-0 shadow-lg shadow-navy-100 hover:shadow-xl hover:shadow-navy-100/50 transition-all duration-500">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-gold to-gold-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-gold-500/20">
                  <Target className="w-7 h-7 text-navy-900" />
                </div>
                <h3 className="text-xl font-bold text-navy-900 mb-3">Holistic Development</h3>
                <p className="text-navy-600 leading-relaxed">Our programs address both personal and professional growth, ensuring a well-rounded development experience.</p>
              </CardContent>
            </Card>
            
            <Card className="group border-0 shadow-lg shadow-navy-100 hover:shadow-xl hover:shadow-navy-100/50 transition-all duration-500">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-green to-green-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-green-500/20">
                  <Users className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-navy-900 mb-3">Supportive Community</h3>
                <p className="text-navy-600 leading-relaxed">Join a community of like-minded individuals who are dedicated to supporting each other's success.</p>
              </CardContent>
            </Card>
            
            <Card className="group border-0 shadow-lg shadow-navy-100 hover:shadow-xl hover:shadow-navy-100/50 transition-all duration-500">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-red to-red-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-red-500/20">
                  <Check className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-navy-900 mb-3">Continuous Learning</h3>
                <p className="text-navy-600 leading-relaxed">Stay updated with the latest strategies and knowledge through our ongoing learning opportunities.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-navy-50 to-gold-50/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-6">
            Ready to Start Your Journey?
          </h2>
          <p className="text-lg text-navy-600 mb-10">
            Join our recruitment program and take the first step toward a rewarding career in early childhood education.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-brand-gold hover:bg-gold-600 text-navy-900 font-semibold px-8 shadow-xl shadow-gold-500/25">
                Apply Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/development">
              <Button size="lg" variant="outline" className="border-2 border-navy-200 text-navy-700 font-semibold px-8 hover:bg-white">
                Explore Development
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
